import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ChangePasswordCard from "@/components/app/ChangePasswordCard";
import TwoFactorCard from "@/components/app/TwoFactorCard";
import WatchlistManager from "@/components/app/WatchlistManager";
import AssignmentManager from "@/components/app/AssignmentManager";
import DocumentUpload from "@/components/app/DocumentUpload";
import SellerListings from "@/components/app/SellerListings";
import { Building2, FileCheck, FileClock, FileX, AlertTriangle } from "lucide-react";

const fmtMoney = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const StatusBadge = ({ s }) => {
  const map = {
    pending: { label: "Pending Review", cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: FileClock },
    approved: { label: "Approved", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: FileCheck },
    rejected: { label: "Rejected", cls: "text-red-700 bg-red-50 border-red-200", Icon: FileX },
  };
  const it = map[s] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 border text-xs font-overline ${it.cls}`}>
      <it.Icon className="w-3 h-3" /> {it.label}
    </span>
  );
};

export default function DealerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [profileStatus, setProfileStatus] = useState("loading");
  const [myNotes, setMyNotes] = useState([]);
  const [activeTab, setActiveTab] = useState("profile");

  const loadProfile = async () => {
    try {
      const r = await api.get("/dealer/profile");
      setProfile(r.data.profile);
      setProfileStatus(r.data.status);
      if (r.data.status === "approved") setActiveTab((t) => (t === "profile" ? "listings" : t));
    } catch {
      setProfileStatus("error");
    }
  };

  const loadNotes = async () => {
    try {
      const r = await api.get("/dealer/notes");
      setMyNotes(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadProfile();
    loadNotes();
  }, []);

  if (profileStatus === "loading") {
    return <div className="px-4 md:px-8 py-10 text-muted-foreground font-mono-data">Loading…</div>;
  }

  return (
    <div className="px-4 md:px-8 py-10 max-w-6xl mx-auto" data-testid="dealer-dashboard">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-overline text-accent">Dealer Console</div>
          <h1 className="font-display text-3xl">{profile?.business_name || user.name}</h1>
        </div>
        <ProfileStatusPill status={profileStatus} />
      </div>

      {profileStatus !== "approved" && (
        <div className="border border-amber-200 bg-amber-50 p-4 mb-6 text-sm flex items-start gap-2" data-testid="dealer-status-banner">
          <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-overline text-amber-800">
              {profileStatus === "missing" ? "Complete your dealer profile" :
               profileStatus === "pending_verification" ? "Awaiting admin verification" :
               profileStatus === "rejected" ? "Application not approved — contact support" :
               profileStatus === "suspended" ? "Account suspended — contact support" :
               profileStatus}
            </div>
            <p className="text-amber-700 mt-1 text-xs">
              {profileStatus === "missing"
                ? "Fill out your business details below to start the verification process."
                : profileStatus === "pending_verification"
                ? "Our team is reviewing your business credentials and documents. You'll be notified once approved."
                : profileStatus === "rejected"
                ? "Your application was not approved. You can update your profile and resubmit."
                : profileStatus === "suspended"
                ? "Your dealer privileges are paused. Please reach out to admin to reinstate."
                : ""}
            </p>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="rounded-none bg-transparent border-b border-border w-full justify-start h-auto p-0">
          <TabsTrigger value="profile" data-testid="dealer-tab-profile" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Profile</TabsTrigger>
          <TabsTrigger value="listings" data-testid="dealer-tab-listings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">My Listings {myNotes.length > 0 && `(${myNotes.length})`}</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="dealer-tab-alerts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Watchlist</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="dealer-tab-transfers" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Transfers</TabsTrigger>
          <TabsTrigger value="settings" data-testid="dealer-tab-settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="pt-6">
          <DealerProfileForm profile={profile} onSaved={(p) => { setProfile(p); loadProfile(); }} />
        </TabsContent>

        <TabsContent value="listings" className="pt-6">
          {profileStatus === "approved"
            ? <SellerListings onMessagesClick={(offerId) => navigate(`/messages?offer_id=${offerId}`)} />
            : <div className="border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="dealer-listings-locked">
                You&apos;ll be able to list notes once your dealer profile is approved.
              </div>}
        </TabsContent>

        <TabsContent value="alerts" className="pt-6">
          <WatchlistManager />
        </TabsContent>

        <TabsContent value="transfers" className="pt-6">
          <AssignmentManager scope="mine" />
        </TabsContent>

        <TabsContent value="settings" className="pt-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-border p-8 space-y-4">
              <div className="font-overline text-accent">Account</div>
              <div>
                <div className="font-overline text-muted-foreground">Email</div>
                <div className="font-mono-data">{user.email}</div>
              </div>
              <div>
                <div className="font-overline text-muted-foreground">Role</div>
                <div className="font-mono-data uppercase">{user.role}</div>
              </div>
            </div>
            <ChangePasswordCard />
            <TwoFactorCard />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileStatusPill({ status }) {
  const map = {
    approved: { label: "Approved", cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
    pending_verification: { label: "Pending Verification", cls: "bg-amber-50 border-amber-200 text-amber-700" },
    missing: { label: "Setup Required", cls: "bg-gray-50 border-gray-200 text-gray-700" },
    rejected: { label: "Rejected", cls: "bg-red-50 border-red-200 text-red-700" },
    suspended: { label: "Suspended", cls: "bg-red-50 border-red-200 text-red-700" },
  };
  const it = map[status] || map.missing;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1.5 border text-xs font-overline ${it.cls}`} data-testid="dealer-profile-status">
      <Building2 className="w-3 h-3" /> {it.label}
    </span>
  );
}

function DealerProfileForm({ profile, onSaved }) {
  const [form, setForm] = useState({
    business_name: "", dba_name: "", contact_name: "", phone: "", ein: "",
    dealer_license: "", finance_license: "",
    business_address: "", state: "", website: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        business_name: profile.business_name || "",
        dba_name: profile.dba_name || "",
        contact_name: profile.contact_name || "",
        phone: profile.phone || "",
        ein: profile.ein || "",
        dealer_license: profile.dealer_license || "",
        finance_license: profile.finance_license || "",
        business_address: profile.business_address || "",
        state: profile.state || "",
        website: profile.website || "",
        notes: profile.notes || "",
      });
    }
  }, [profile]);

  const save = async () => {
    if (!form.business_name.trim()) { toast.error("Dealership legal name required"); return; }
    setBusy(true);
    try {
      const r = await api.put("/dealer/profile", form);
      const wasRejected = profile?.status === "rejected" || profile?.status === "suspended";
      toast.success(wasRejected ? "Profile resubmitted — awaiting admin re-verification"
                                   : (profile ? "Profile updated" : "Profile submitted — awaiting admin verification"));
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const profileStatus = profile?.status;
  const isRejected = profileStatus === "rejected" || profileStatus === "suspended";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="dealer-profile-form">
      {isRejected && (
        <div className="border border-red-300 bg-red-50 dark:bg-red-900/10 p-4 text-sm" data-testid="dealer-rejected-banner">
          <div className="font-overline text-red-800 dark:text-red-300">
            {profileStatus === "rejected" ? "Application Rejected" : "Account Suspended"}
          </div>
          {profile?.rejection_reason && (
            <div className="text-red-900 dark:text-red-200 mt-2"><b>Reason:</b> {profile.rejection_reason}</div>
          )}
          <p className="text-red-800 dark:text-red-200 mt-2">
            Update the fields below and resubmit. Your application will return to <b>Pending Verification</b> for our team to re-review.
          </p>
        </div>
      )}
      <div className="border border-border p-6">
        <div className="font-overline text-accent mb-4">Dealership Verification</div>
        <p className="text-xs text-muted-foreground mb-4">
          We&apos;ll review your business credentials and approve your account. You can save and update this form before final submission.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Dealership Legal Name *" tid="dlr-business-name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} />
          <Field label="DBA Name (if any)" tid="dlr-dba" value={form.dba_name} onChange={(v) => setForm({ ...form, dba_name: v })} />
          <Field label="Primary Contact" tid="dlr-contact-name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
          <Field label="Phone (E.164)" tid="dlr-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+15551234567" />
          <Field label="EIN" tid="dlr-ein" value={form.ein} onChange={(v) => setForm({ ...form, ein: v })} placeholder="12-3456789" />
          <Field label="Dealer License #" tid="dlr-license" value={form.dealer_license} onChange={(v) => setForm({ ...form, dealer_license: v })} />
          <Field label="Finance License # (if applicable)" tid="dlr-finance-license" value={form.finance_license} onChange={(v) => setForm({ ...form, finance_license: v })} />
          <Field label="State" tid="dlr-state" value={form.state} onChange={(v) => setForm({ ...form, state: v.toUpperCase() })} placeholder="TX" />
          <div className="md:col-span-2">
            <Field label="Business Address" tid="dlr-address" value={form.business_address} onChange={(v) => setForm({ ...form, business_address: v })} />
          </div>
          <Field label="Website" tid="dlr-website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://" />
          <div className="md:col-span-2">
            <Label className="font-overline">Notes (anything our admin should know)</Label>
            <Textarea className="rounded-none mt-2" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="dlr-notes" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={save} disabled={busy} className="rounded-none uppercase tracking-wider h-11" data-testid="dlr-save-profile">
            {busy ? "Saving…" : isRejected ? "Resubmit for Verification" : profile ? "Update Profile" : "Submit for Verification"}
          </Button>
        </div>
      </div>

      {profile?.dealer_id && (
        <div className="border border-border p-6" data-testid="dealer-documents-section">
          <DocumentUpload
            entityType="dealer_profile"
            entityId={profile.dealer_id}
            title="Verification Documents"
            docTypes={[
              { value: "dealer_license", label: "Dealer License" },
              { value: "finance_license", label: "Finance License" },
              { value: "ein_document", label: "EIN Document" },
              { value: "w9", label: "W-9" },
              { value: "certificate_of_insurance", label: "Certificate of Insurance" },
              { value: "additional", label: "Additional Supporting Documents" },
            ]}
          />
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, tid, placeholder }) {
  return (
    <div>
      <Label className="font-overline">{label}</Label>
      <Input className="rounded-none mt-2" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={tid} />
    </div>
  );
}

function DealerListings({ notes, navigate }) {
  if (!notes || notes.length === 0) {
    return <div className="border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="dealer-listings-empty">
      No notes submitted yet. Use &quot;Submit Note&quot; to list your first.
    </div>;
  }
  return (
    <div className="border border-border" data-testid="dealer-listings">
      {notes.map((n) => (
        <div key={n.note_id} className="border-b border-border last:border-b-0 p-5 grid grid-cols-2 md:grid-cols-6 gap-3 items-center" data-testid={`dealer-note-row-${n.note_id}`}>
          <div className="md:col-span-2">
            <div className="font-mono-data text-sm">{n.vehicle_year} {n.vehicle_make} {n.vehicle_model}</div>
            <div className="text-xs text-muted-foreground uppercase">{n.state} · Grade {n.grade}</div>
          </div>
          <div>
            <div className="font-overline text-muted-foreground">Yield</div>
            <div className="font-mono-data">{n.estimated_yield}%</div>
          </div>
          <div>
            <div className="font-overline text-muted-foreground">Ask</div>
            <div className="font-mono-data">{fmtMoney(n.asking_price)}</div>
          </div>
          <div>
            <StatusBadge s={n.moderation?.status || "pending"} />
            {n.moderation?.rejection_reason && (
              <div className="text-xs text-red-700 mt-1">Reason: {n.moderation.rejection_reason}</div>
            )}
          </div>
          <div className="text-right">
            {n.listed && (
              <Button size="sm" variant="outline" className="rounded-none text-xs h-8"
                onClick={() => navigate(`/notes/${n.note_id}`)} data-testid={`dealer-view-${n.note_id}`}>View live</Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const CATEGORY_OPTIONS = [
  { value: "performing", label: "Performing" },
  { value: "semi_performing", label: "Semi-Performing" },
  { value: "non_performing", label: "Non-Performing" },
  { value: "repo_ready", label: "Repo-Ready" },
];
const GRADES = ["A", "B", "C", "D"];

function DealerNoteForm({ onSubmitted }) {
  const [f, setF] = useState({
    category: "performing", grade: "B",
    vehicle_year: "", vehicle_make: "", vehicle_model: "", vehicle_mileage: "",
    state: "", original_loan_amount: "", remaining_balance: "",
    interest_rate: "", weekly_payment: "", remaining_term_weeks: "",
    payments_made: 0, late_payments: 0,
    estimated_yield: "", asking_price: "",
    payment_frequency: "weekly", description: "",
  });
  const [busy, setBusy] = useState(false);
  const [submittedNote, setSubmittedNote] = useState(null);

  const submit = async () => {
    const required = ["vehicle_year","vehicle_make","vehicle_model","state","original_loan_amount","remaining_balance","interest_rate","weekly_payment","remaining_term_weeks","estimated_yield","asking_price"];
    for (const k of required) {
      if (!f[k] && f[k] !== 0) { toast.error(`Missing: ${k.replace(/_/g," ")}`); return; }
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        vehicle_year: Number(f.vehicle_year),
        vehicle_mileage: Number(f.vehicle_mileage || 0),
        original_loan_amount: Number(f.original_loan_amount),
        remaining_balance: Number(f.remaining_balance),
        interest_rate: Number(f.interest_rate),
        weekly_payment: Number(f.weekly_payment),
        remaining_term_weeks: Number(f.remaining_term_weeks),
        payments_made: Number(f.payments_made || 0),
        late_payments: Number(f.late_payments || 0),
        estimated_yield: Number(f.estimated_yield),
        asking_price: Number(f.asking_price),
        state: (f.state || "").toUpperCase(),
      };
      const r = await api.post("/dealer/notes", payload);
      toast.success("Submitted. Please upload supporting documents below.");
      setSubmittedNote(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (submittedNote) {
    return (
      <div className="border border-border p-6 max-w-4xl space-y-6" data-testid="dealer-note-submitted">
        <div>
          <div className="font-overline text-accent">Submitted for Review</div>
          <h3 className="font-display text-2xl mt-1">{submittedNote.vehicle_year} {submittedNote.vehicle_make} {submittedNote.vehicle_model}</h3>
          <p className="text-xs text-muted-foreground mt-2">Upload photos and supporting documents. Admin will review the listing & docs together.</p>
        </div>
        <DocumentUpload
          entityType="note"
          entityId={submittedNote.note_id}
          title="Note Documents"
          docTypes={[
            { value: "vehicle_photo", label: "Vehicle Photo" },
            { value: "ric", label: "Retail Installment Contract" },
            { value: "payment_history", label: "Payment History" },
            { value: "title", label: "Title" },
            { value: "insurance", label: "Insurance" },
            { value: "inspection", label: "Inspection" },
            { value: "additional", label: "Additional Supporting Documents" },
          ]}
        />
        <Button onClick={() => { setSubmittedNote(null); onSubmitted?.(); }} className="rounded-none uppercase tracking-wider"
          data-testid="dnf-done">Done — Back to Listings</Button>
      </div>
    );
  }

  return (
    <div className="border border-border p-6 max-w-4xl space-y-4" data-testid="dealer-note-form">
      <div className="font-overline text-accent">Submit Note for Review</div>
      <p className="text-xs text-muted-foreground">All listings go through admin moderation before going live on the marketplace.</p>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label className="font-overline">Category</Label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger className="rounded-none mt-2" data-testid="dnf-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-overline">Grade</Label>
          <Select value={f.grade} onValueChange={(v) => setF({ ...f, grade: v })}>
            <SelectTrigger className="rounded-none mt-2" data-testid="dnf-grade"><SelectValue /></SelectTrigger>
            <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-overline">Payment Freq.</Label>
          <Select value={f.payment_frequency} onValueChange={(v) => setF({ ...f, payment_frequency: v })}>
            <SelectTrigger className="rounded-none mt-2" data-testid="dnf-freq"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="biweekly">Bi-weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Field label="Year" tid="dnf-year" value={f.vehicle_year} onChange={(v) => setF({ ...f, vehicle_year: v })} />
        <Field label="Make" tid="dnf-make" value={f.vehicle_make} onChange={(v) => setF({ ...f, vehicle_make: v })} />
        <Field label="Model" tid="dnf-model" value={f.vehicle_model} onChange={(v) => setF({ ...f, vehicle_model: v })} />
        <Field label="Mileage" tid="dnf-mileage" value={f.vehicle_mileage} onChange={(v) => setF({ ...f, vehicle_mileage: v })} />
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Field label="State" tid="dnf-state" value={f.state} onChange={(v) => setF({ ...f, state: v })} placeholder="TX" />
        <Field label="Original Loan $" tid="dnf-orig" value={f.original_loan_amount} onChange={(v) => setF({ ...f, original_loan_amount: v })} />
        <Field label="Remaining Bal $" tid="dnf-bal" value={f.remaining_balance} onChange={(v) => setF({ ...f, remaining_balance: v })} />
        <Field label="Interest %" tid="dnf-int" value={f.interest_rate} onChange={(v) => setF({ ...f, interest_rate: v })} />
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Field label="Weekly Payment $" tid="dnf-wp" value={f.weekly_payment} onChange={(v) => setF({ ...f, weekly_payment: v })} />
        <Field label="Remaining Weeks" tid="dnf-weeks" value={f.remaining_term_weeks} onChange={(v) => setF({ ...f, remaining_term_weeks: v })} />
        <Field label="Estimated Yield %" tid="dnf-yield" value={f.estimated_yield} onChange={(v) => setF({ ...f, estimated_yield: v })} />
        <Field label="Asking $" tid="dnf-ask" value={f.asking_price} onChange={(v) => setF({ ...f, asking_price: v })} />
      </div>

      <div>
        <Label className="font-overline">Description (optional)</Label>
        <Textarea className="rounded-none mt-2" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="dnf-desc" />
      </div>

      <Button onClick={submit} disabled={busy} className="rounded-none uppercase tracking-wider h-11" data-testid="dnf-submit">
        {busy ? "Submitting…" : "Submit for Review"}
      </Button>
    </div>
  );
}
